import { mutate } from "swr";

import * as fetcher from "../fetcher";
import { mutateWithStartKey } from "../mutateWithStartKey";
import { type Appointment, type ExtendedAppointment } from "./types";

type AppointmentAction = "cancel" | "no-show" | "start" | "end";

interface AssignAppointmentResponse {
  appointmentId: number;
}

export const updateAppointments = async (
  action: AppointmentAction,
  appointment?: ExtendedAppointment
): Promise<Appointment | null> => {
  if (!appointment) {
    return null;
  }
  const res = await fetcher.core.update(`/appointments/action`, {
    action,
    appointmentId: appointment.appointmentId,
    orderNumber: appointment.orderNumber,
  });
  mutate(`/opportunities/${appointment.opportunityId}/history`);
  mutate(`/${appointment.opportunityId}/appointments`);
  mutate(`/appointments/${appointment.id}/opportunity`);
  mutateWithStartKey("/opportunities/appointments");

  return (await res.json()) as Appointment;
};

export const optimisticUpdateAppointmentStatus = (
  appointmentId: string,
  nextStatus: Appointment["appointmentStatus"],
  opportunityId?: number
) => {
  if (!opportunityId || !appointmentId) {
    return;
  }
  mutate(
    `/opportunities/${opportunityId}/appointments`,
    (existing: Appointment[] | undefined) =>
      existing?.map((a) =>
        a.id === appointmentId ? { ...a, appointmentStatus: nextStatus } : a
      ),
    false
  );
};

export const assignAppointment = async (
  appointmentId?: number,
  opportunityId?: number,
  oldEmail?: string,
  newEmail?: string,
  mutateSearchAppointments?: () => void
): Promise<AssignAppointmentResponse | null> => {
  if (!appointmentId || !newEmail || oldEmail === newEmail) {
    return null;
  }
  const res = await fetcher.core.patch(`/appointments/assign`, {
    appointmentId,
    coworkerEmail: newEmail,
  });

  const assignResponse = (await res.json()) as AssignAppointmentResponse;

  // Optimistically update appointment in project
  mutate(
    `/${opportunityId}/appointments`,
    (existing: Appointment[] | undefined) =>
      existing?.map((a) =>
        a.appointmentId === assignResponse.appointmentId
          ? { ...a, assignedCoworker: newEmail }
          : a
      ),
    { revalidate: false }
  );

  // Delay revalidation to allow backend to fully process the change
  await new Promise((resolve) => setTimeout(resolve, 2000));

  if (mutateSearchAppointments) {
    mutateSearchAppointments();
  }
  mutate(`/opportunities/${opportunityId}/history`);
  mutate(`/${opportunityId}/appointments`);
  mutate(`/appointments/${appointmentId}/opportunity`);
  mutateWithStartKey("/opportunities/appointments");

  return assignResponse;
};
